import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';

import { useAuth } from '../../context/AuthContext';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, loginWithGoogle, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { register, handleSubmit, formState: { errors } } = useForm();

  const from = location.state?.from?.pathname || '/';

  const onSubmit = async (data) => {
    const result = await login(data.login, data.password);
    
    if (result.success) {
      toast.success('تم تسجيل الدخول بنجاح');
      
      // Redirect based on user role
      if (result.user?.roles) {
        if (result.user.roles.some(role => role.name === 'admin')) {
          navigate('/admin/dashboard', { replace: true });
        } else if (result.user.roles.some(role => role.name === 'vendor')) {
          navigate('/vendor/dashboard', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        navigate(from, { replace: true });
      }
    } else {
      toast.error(result.message);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      
      const googleData = {
        google_id: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        avatar: decoded.picture
      };

      const result = await loginWithGoogle(googleData);
      
      if (result.success) {
        toast.success('تم تسجيل الدخول بنجاح');
        
        // Redirect based on user role
        if (result.user?.roles) {
          if (result.user.roles.some(role => role.name === 'admin')) {
            navigate('/admin/dashboard', { replace: true });
          } else if (result.user.roles.some(role => role.name === 'vendor')) {
            navigate('/vendor/dashboard', { replace: true });
          } else {
            navigate(from, { replace: true });
          }
        } else {
          navigate(from, { replace: true });
        }
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('حدث خطأ في تسجيل الدخول بحساب Google');
    }
  };

  const handleGoogleError = () => {
    toast.error('فشل تسجيل الدخول بحساب Google');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto flex justify-center">
            <img 
              src="/logo.png" 
              alt="أمازول" 
              className="h-16 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            تسجيل الدخول
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            أو{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              إنشاء حساب جديد
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label className="form-label">البريد الإلكتروني أو رقم الجوال</label>
              <input
                type="text"
                className="form-input"
                placeholder="أدخل بريدك الإلكتروني أو رقم الجوال"
                {...register('login', {
                  required: 'البريد الإلكتروني أو رقم الجوال مطلوب'
                })}
              />
              {errors.login && (
                <p className="text-red-500 text-sm mt-1">{errors.login.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">كلمة المرور</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input pl-10"
                  placeholder="أدخل كلمة المرور"
                  {...register('password', {
                    required: 'كلمة المرور مطلوبة',
                    minLength: {
                      value: 6,
                      message: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل'
                    }
                  })}
                />
                <button
                  type="button"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="mr-2 block text-sm text-gray-900">
                تذكرني
              </label>
            </div>

            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                نسيت كلمة المرور؟
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
            </button>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 text-gray-500">أو</span>
            </div>
          </div>

          {/* Google Login Button */}
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              text="signin_with"
              locale="ar"
              width="384"
            />
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              هل أنت بائع؟{' '}
              <Link
                to="/vendor/register"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                انضم كبائع
              </Link>
            </p>
          </div>
        </form>

        {/* Demo Accounts */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="text-sm font-medium text-blue-900 mb-2">حسابات تجريبية:</h3>
          <div className="text-xs text-blue-800 space-y-1">
            <p><strong>عميل:</strong> customer@test.com / 123456</p>
            <p><strong>بائع:</strong> vendor@test.com / 123456</p>
            <p><strong>مدير:</strong> admin@test.com / 123456</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;